import React, { useState, useEffect } from 'react';
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TabsContent as UITabsContent } from "@/components/ui/tabs"; // Renamed to avoid conflict
import { Class, Enrollment } from "@/types/classTypes";
import { Calendar, Users, ClipboardList, Info } from "lucide-react";
import { AttendanceService } from "@/services/attendanceService";
import { InfoTab } from './InfoTab'; // Changed from default to named import
import ScheduleTab from './ScheduleTab';
import StudentsTab from './StudentsTab';
import AttendanceTab from './AttendanceTab';
import WaitlistManagementDialog from "../WaitlistManagementDialog";
import { toast } from "sonner";
import { useAuth } from '@/hooks/useAuth';

interface TabsContentProps {
  classData: Class;
  enrollments: Enrollment[];
  organizationId: string;
  onScheduleUpdated?: () => Promise<void>;
  onEnrollmentUpdated: () => Promise<void>;
}

const TabsContent: React.FC<TabsContentProps> = ({
  classData,
  enrollments,
  organizationId,
  onScheduleUpdated,
  onEnrollmentUpdated
}) => {
  const [isWaitlistDialogOpen, setIsWaitlistDialogOpen] = useState(false);
  const [attendanceRecords, setAttendanceRecords] = useState<any[]>([]);
  const [isLoadingAttendance, setIsLoadingAttendance] = useState(false);
  const { user } = useAuth();

  const handleWaitlistManage = () => {
    setIsWaitlistDialogOpen(true);
  };

  const handleSendReminder = async (studentIds: string[]) => {
    try {
      // In a real implementation, this would send notifications to parents
      console.log("Sending reminders to students:", studentIds);
      toast.success(`Attendance reminders sent to ${studentIds.length} students`);
      return Promise.resolve();
    } catch (error) {
      console.error("Error sending reminders:", error);
      toast.error("Failed to send reminders");
      return Promise.reject(error);
    }
  };

  const loadAttendanceRecords = async () => {
    if (!classData.id) return;
    
    setIsLoadingAttendance(true);
    try {
      const records = await AttendanceService.getAttendanceByClass(classData.id);
      setAttendanceRecords(records);
    } catch (error) {
      console.error("Error loading attendance records:", error);
    } finally {
      setIsLoadingAttendance(false);
    }
  };

  // Get active and waitlisted enrollments
  const activeEnrollments = enrollments.filter(e => e.status === 'active');
  const waitlistedEnrollments = enrollments.filter(e => 
    e.status === 'waitlisted'
  );

  return (
    <>
      <Tabs defaultValue="info" className="w-full">
        <TabsList className="grid grid-cols-4">
          <TabsTrigger value="info">
            <Info className="h-4 w-4 mr-2" />
            Info
          </TabsTrigger>
          <TabsTrigger value="schedule">
            <Calendar className="h-4 w-4 mr-2" />
            Schedule
          </TabsTrigger>
          <TabsTrigger value="students">
            <Users className="h-4 w-4 mr-2" />
            Students
          </TabsTrigger>
          <TabsTrigger value="attendance" onClick={loadAttendanceRecords}>
            <ClipboardList className="h-4 w-4 mr-2" />
            Attendance
          </TabsTrigger>
        </TabsList>
        
        <UITabsContent value="info">
          <InfoTab 
            classData={classData} 
            enrollments={enrollments}
            onWaitlistManage={handleWaitlistManage}
          />
        </UITabsContent>
        
        <UITabsContent value="schedule">
          <ScheduleTab 
            organizationId={organizationId} 
            initialClassId={classData.id} 
          />
        </UITabsContent>
        
        <UITabsContent value="students">
          <StudentsTab 
            enrollments={enrollments} 
            currentUserId={user?.id || ''} 
            onEnrollmentUpdated={onEnrollmentUpdated}
            onManageWaitlist={handleWaitlistManage}
          />
        </UITabsContent>
        
        <UITabsContent value="attendance">
          <AttendanceTab 
            classData={classData}
            organizationId={organizationId}
            enrollments={enrollments}
            attendanceRecords={attendanceRecords}
            isLoadingAttendance={isLoadingAttendance}
            onSendReminder={handleSendReminder}
          />
        </UITabsContent>
      </Tabs>
      
      <WaitlistManagementDialog
        open={isWaitlistDialogOpen}
        onOpenChange={setIsWaitlistDialogOpen}
        waitlistedEnrollments={waitlistedEnrollments}
        classId={classData.id}
        organizationId={organizationId}
        maxClassSize={classData.max_students || 0}
        currentEnrolled={activeEnrollments.length}
        onEnrollmentUpdated={onEnrollmentUpdated}
      />
    </>
  );
};

export default TabsContent;
